<script setup lang="ts">
import {ref, computed, watch, nextTick} from 'vue';
import {formatCurrency} from '@/ts/utils/number-format';
import {useProfileDateFormat} from '@/ts/composables/stateful/use-profile-date-format';
import {useI18n} from 'vue-i18n';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import LoaderAnimation from '@/vue/molecules/loader-animation.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import type{SubscriptionResponse} from '@/ts/types/dto/user-dto';
import {AppError} from '@/ts/utils/errors';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';
import {sleep} from '@/ts/utils/pure-functions';
import {planDisplay} from '@/ts/utils/plan-display';

 
type StripeInstance = any;
 
type StripeCardElement = any;

interface StripeElements {
  stripe: StripeInstance;
  cardElement: StripeCardElement;
}

declare global {
  interface Window {

    Stripe?(key: string, options?: {locale?: string}): StripeInstance;
  }
}

const props = defineProps<{
  modelValue: boolean;
  licenseId: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  paymentSuccess: [];
  paymentPending: [];
  paymentError: [error: string];
  subscriptionSynced: [];
}>();

enum PaymentState {
  LOADING = 'loading',
  FORM = 'form',
  SUCCESS = 'success',
  ERROR = 'error',
}

// After Stripe confirms payment, we poll the BE to check if the webhook created the contract.
// If webhook is delayed/fails, user sees "payment pending" message after max attempts.
const POLLING_MAX_ATTEMPTS = 10;
const POLLING_INTERVAL_MS = 2000;

const api = useApi();
const logger = useLogger('payment-dialog');
const {t, locale} = useI18n();
// Prices the way the reader's country writes money, in the currency the backend gives.
const datePatterns = useProfileDateFormat();

const paymentState = ref<PaymentState>(PaymentState.LOADING);
const subscriptionResponse = ref<SubscriptionResponse | null>(null);

// Billing period label derived from the product's recurring interval; empty for one-time products.
const productPeriodLabel = computed((): string => {
  const {periodKey} = planDisplay(subscriptionResponse.value?.product?.recurring);
  return periodKey ? t(periodKey) : '';
});
const error = ref<string | null>(null);
const formError = ref<string | null>(null); // Error shown in form without unmounting Card Element
const stripeLoaded = ref(false);
const stripeCardElement = ref<StripeElements | null>(null);
const isProcessing = ref(false);
const processingMessage = ref<'processing_payment' | 'confirming_subscription'>('processing_payment');

const dialogValue = computed({
  get: () => props.modelValue,
  set: (value: boolean) => { emit('update:modelValue', value); },
});

const closeDialog = (): void => {
  if (stripeCardElement.value?.cardElement) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      stripeCardElement.value.cardElement.unmount();
    } catch {
      // Element may already be unmounted
    }
  }

  dialogValue.value = false;
  subscriptionResponse.value = null;
  stripeCardElement.value = null;
  error.value = null;
  formError.value = null;
  paymentState.value = PaymentState.LOADING;
  stripeLoaded.value = false;
};

const getCssVar = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const setupStripeElements = (): void => {
  if (!window.Stripe || !subscriptionResponse.value) {
    return;
  }

  paymentState.value = PaymentState.FORM;

  void nextTick(() => {
    const cardElementContainer = document.querySelector('#card-element');
    if (!cardElementContainer) {
      setTimeout(setupStripeElements, 100);
      return;
    }

    // Stripe defaults to the browser language, which ignores the language the profile is set to.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const stripe: StripeInstance = window.Stripe?.(
      subscriptionResponse.value?.publicKey ?? '',
      {locale: locale.value},
    );

    // Read theme from CSS variables to match the app's design system
    const colorPrimary = getCssVar('--Primary-primary-color-1');
    const colorBackground = getCssVar('--Neutral-UI-neutral-ui-10');
    const colorText = getCssVar('--Neutral-UI-neutral-ui-100');
    const colorDanger = getCssVar('--Primary-primary-color-3');
    const colorPlaceholder = getCssVar('--Neutral-UI-neutral-ui-50');
    const fontFamily = getCssVar('--Font-Primary-Font');

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,
       @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
    const elements = stripe.elements({
      appearance: {
        theme: 'flat',
        variables: {
          colorPrimary,
          colorBackground,
          colorText,
          colorDanger,
          fontFamily,
          spacingUnit: '8px',
          borderRadius: '8px',
        },
      },
    });

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,
       @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
    const cardElement: StripeCardElement = elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          color: colorText,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '::placeholder': {
            color: colorPlaceholder,
          },
        },
      },
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    cardElement.mount('#card-element');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    stripeCardElement.value = {stripe, cardElement};
  });
};

const initializeStripe = (): void => {
  if (!subscriptionResponse.value) {
    return;
  }

  if (window.Stripe) {
    stripeLoaded.value = true;
    setupStripeElements();
  } else {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = (): void => {
      stripeLoaded.value = true;
      setupStripeElements();
    };
    script.onerror = (): void => {
      // The shown copy is the generic one, so keep the actual failure mode in the log.
      logger.error('Failed to load Stripe.js from', script.src)();
      error.value = t('payment_error_generic');
      paymentState.value = PaymentState.ERROR;
    };
    document.head.appendChild(script);
  }
};

const pollForActiveContract = async(): Promise<boolean> => {
  for (let attempt = 0; attempt < POLLING_MAX_ATTEMPTS; attempt++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const user = await api.me.getMe();
      // Only consider paid subscriptions, not gifted licenses
      if (user.lastContract?.paymentSubscription) {
        return true;
      }
    } catch {
      // Ignore errors during polling, just retry
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLLING_INTERVAL_MS);
  }
  return false;
};

const createSubscription = async(): Promise<void> => {
  paymentState.value = PaymentState.LOADING;
  error.value = null;

  try {
    const response = await api.me.createSubscriptionForLicense(props.licenseId);
    subscriptionResponse.value = response;
    initializeStripe();
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : null;
    error.value = errorMessage;
    paymentState.value = PaymentState.ERROR;

    // Check if user already has an active subscription (synced from Stripe)
    if (err instanceof AppError && err.statusCode === ErrorCodesDto.ACTIVE_SUBSCRIPTION_EXISTS) {
      emit('subscriptionSynced');
      return;
    }

    if (errorMessage) {
      emit('paymentError', errorMessage);
    }
  }
};

// eslint-disable-next-line max-statements
const processPayment = async(): Promise<void> => {
  if (!stripeCardElement.value || !subscriptionResponse.value) {
    return;
  }

  // Clear any previous form errors
  formError.value = null;

  isProcessing.value = true;
  processingMessage.value = 'processing_payment';

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const {stripe, cardElement} = stripeCardElement.value;
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,
       @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
    const {error: confirmError} = await stripe.confirmCardPayment(subscriptionResponse.value.clientSecret, {
      // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-unsafe-assignment
      payment_method: {card: cardElement},
    });

    if (confirmError) {
      // Show error in form without unmounting Card Element (keeps user's data)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      formError.value = confirmError.message || null;
      isProcessing.value = false;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (confirmError.message) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        emit('paymentError', confirmError.message);
      }
    } else {
      // Payment confirmed by Stripe, now poll for contract creation
      processingMessage.value = 'confirming_subscription';

      const contractFound = await pollForActiveContract();
      isProcessing.value = false;

      if (contractFound) {
        paymentState.value = PaymentState.SUCCESS;
        emit('paymentSuccess');
      } else {
        // Timeout - payment processed but contract not yet created
        closeDialog();
        emit('paymentPending');
      }
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : null;
    error.value = errorMessage;
    paymentState.value = PaymentState.ERROR;
    isProcessing.value = false;
    if (errorMessage) {
      emit('paymentError', errorMessage);
    }
  }
};

const handleDialogOpen = (): void => {
  if (props.modelValue && !subscriptionResponse.value) {
    void createSubscription();
  }
};

const retryPayment = (): void => {
  void createSubscription();
};

const formatPrice = (price: number | undefined, currency: string | undefined): string => {
  if (!price || !currency) {
    return '';
  }
  return formatCurrency(price, currency, datePatterns.value.numberLocale);
};

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    handleDialogOpen();
  }
}, {immediate: true});
</script>

<template>
  <balm-dialog
    v-model="dialogValue"
    type="fit-content"
    theme="grey-10"
    :no-x="isProcessing"
    @update:model-value="closeDialog"
  >
    <div class="payment-dialog">
      <!-- Processing Overlay (shown on top of form) -->
      <div
        v-if="isProcessing"
        class="processing-overlay"
      >
        <div class="loading-content">
          <div class="form-icon">
            <svg-illustration name="payment-sun.svg"/>
          </div>
          <h3 class="loading-title">
            {{ t(processingMessage) }}
          </h3>
          <div class="loading-icon">
            <loader-animation/>
          </div>
          <p class="loading-description">
            {{ t(processingMessage === 'processing_payment' ? 'please_wait_processing' : 'confirming_subscription_description') }}
          </p>
        </div>
      </div>

      <!-- Loading State (initial) -->
      <div
        v-if="paymentState === PaymentState.LOADING"
        class="loading-overlay"
      >
        <div class="loading-content">
          <div class="form-icon">
            <svg-illustration name="payment-sun.svg"/>
          </div>
          <h3 class="loading-title">
            {{ t('setting_up_payment') }}
          </h3>
          <div class="loading-icon">
            <loader-animation/>
          </div>
          <p class="loading-description">
            {{ t('preparing_secure_payment') }}
          </p>
        </div>
      </div>

      <!-- Payment Form State -->
      <div
        v-else-if="paymentState === PaymentState.FORM"
        class="payment-state form-state"
      >
        <div class="form-header">
          <div class="form-icon">
            <svg-illustration name="payment-sun.svg"/>
          </div>
          <h2 class="form-title">
            {{ t('complete_your_purchase') }}
          </h2>
          <div class="product-info">
            <div class="product-name">
              {{ subscriptionResponse?.product?.name }}
            </div>
            <div
              v-if="subscriptionResponse?.product?.description"
              class="product-description"
            >
              {{ subscriptionResponse.product.description }}
            </div>
            <div class="payment-amount">
              <span class="price">{{ formatPrice(subscriptionResponse?.product?.price, subscriptionResponse?.product?.currency) }}</span>
              <span v-if="productPeriodLabel" class="period">{{ productPeriodLabel }}</span>
            </div>
          </div>
        </div>

        <div class="payment-form">
          <div class="card-section">
            <label class="card-label">{{ t('card_details') }}</label>
            <div
              id="card-element"
              class="card-element"
              :class="{'has-error': formError}"
            />
            <div v-if="formError" class="form-error-message">
              {{ formError }}
            </div>
          </div>

          <div class="form-actions">
            <balm-btn
              :label="isProcessing ? t('processing') : t('complete_payment')"
              theme="blue"
              class="pay-button"
              :disable="isProcessing"
              @click="processPayment"
            />
            <balm-btn
              :label="t('cancel')"
              theme="grey"
              class="cancel-button"
              :disable="isProcessing"
              @click="closeDialog"
            />
          </div>
        </div>
      </div>

      <!-- Success State -->
      <div
        v-else-if="paymentState === PaymentState.SUCCESS"
        class="payment-state success-state"
      >
        <div class="form-icon">
          <svg-illustration name="payment-sun.svg"/>
        </div>
        <h2 class="success-title">
          {{ t('payment_processed') }}
        </h2>
        <div class="success-illustration">
          <svg-illustration name="payment-thumbs-up.svg"/>
        </div>
        <p class="success-description">
          {{ t('payment_success_description') }}
        </p>
        <balm-btn
          :label="t('done')"
          theme="blue"
          class="done-button"
          @click="closeDialog"
        />
      </div>

      <!-- Error State -->
      <div
        v-else-if="paymentState === PaymentState.ERROR"
        class="payment-state error-state"
      >
        <div class="state-icon error-icon">
          <svg-illustration name="x-close.svg"/>
        </div>
        <h2 class="state-title error-title">
          {{ t('payment_failed') }}
        </h2>
        <p class="state-description error-description">
          {{ error || t('payment_error_generic') }}
        </p>
        <div class="error-actions">
          <balm-btn
            :label="t('try_again')"
            theme="blue"
            class="full-width"
            @click="retryPayment"
          />
          <balm-btn
            :label="t('cancel')"
            theme="grey"
            class="full-width"
            @click="closeDialog"
          />
        </div>
      </div>
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">
.payment-dialog {
  position: relative;
  overflow: hidden;
  padding: var(--Spacing-spacing-4);
  padding-top: var(--Spacing-spacing-4);
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @include screen-md {
    min-width: 460px;
  }
}

.payment-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  min-height: 240px;
  transition: all 0.3s ease;
  animation: fadeInUp 0.4s ease-out;
}

.state-icon {
  margin-bottom: var(--Spacing-spacing-5);
  display: flex;
  align-items: center;
  justify-content: center;

  &.error-icon {
    width: 80px;
    height: 80px;
    background: var(--Primary-primary-color-3);
    border-radius: 50%;
    box-shadow: 0 8px 24px rgba(203, 109, 82, 0.2);
    animation: errorShake 0.5s ease-out;

    :deep(svg) {
      width: 40px;
      height: 40px;
      fill: white;
    }
  }
}

.state-title {
  @include font-h2;
  margin-bottom: var(--Spacing-spacing-3);
  color: var(--Neutral-UI-neutral-ui-100);
  font-weight: 600;

  &.error-title {
    color: var(--Primary-primary-color-3);
  }
}

.state-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin-bottom: var(--Spacing-spacing-5);
  max-width: 360px;
  line-height: 1.6;

  &.error-description {
    color: var(--Primary-primary-color-3);
    background: rgba(203, 109, 82, 0.05);
    padding: var(--Spacing-spacing-3);
    border-radius: var(--Spacing-spacing-2);
    border: 1px solid rgba(203, 109, 82, 0.1);
  }
}

.form-state {
  align-items: center;
  text-align: center;
  position: relative;
}

.form-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: var(--Spacing-spacing-5);
  padding-bottom: var(--Spacing-spacing-4);
  border-bottom: 2px solid var(--Neutral-UI-neutral-ui-10);
}

.form-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--Spacing-spacing-4);
}

.form-title {
  @include font-display-3;
  margin-bottom: var(--Spacing-spacing-5);
  color: var(--Neutral-UI-neutral-ui-100);
}

.product-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-2);
}

.product-name {
  @include font-body-bold;
  color: var(--Neutral-UI-neutral-ui-80);
}

.product-description {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-60);
  max-width: 300px;
}

.payment-amount {
  display: flex;
  align-items: baseline;
  gap: var(--Spacing-spacing-2);

  .price {
    @include font-metric-lg;
    color: var(--Neutral-UI-neutral-ui-100);
  }

  .period {
    @include font-body;
    color: var(--Neutral-UI-neutral-ui-60);
  }
}

.payment-form {
  width: 100%;
}

.card-section {
  margin-bottom: var(--Spacing-spacing-5);
  text-align: left;
  width: 100%;
}

.card-label {
  @include font-body-sm;
  display: block;
  margin-bottom: var(--Spacing-spacing-3);
  color: var(--Neutral-UI-neutral-ui-90);
  line-height: 100%;
}

.card-element {
  padding: var(--Spacing-spacing-4);
  border: 2px solid var(--Neutral-UI-neutral-ui-15);
  border-radius: var(--Spacing-spacing-3);
  background: var(--Neutral-UI-neutral-ui-0);
  min-height: 56px;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--Primary-primary-color-1);
    box-shadow: 0 4px 12px rgba(93, 131, 178, 0.08);
  }

  &:focus-within {
    border-color: var(--Primary-primary-color-1);
    box-shadow: 0 0 0 4px rgba(93, 131, 178, 0.12);
  }

  &.has-error {
    border-color: var(--Functional-wl-error-text);
  }
}

.form-error-message {
  @include font-body-sm;
  color: var(--Functional-wl-error-text);
  margin-top: var(--Spacing-spacing-2);
  line-height: 1.4;
}

.form-actions,
.error-actions {
  display: flex;
  gap: var(--Spacing-spacing-3);
  flex-direction: column;
  width: 100%;
}

.pay-button,
.cancel-button,
.done-button,
.full-width {
  width: 100%;
}

.success-state {
  min-height: 200px;
  justify-content: center;
  padding: var(--Spacing-spacing-4);
}

.success-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-4);
}

.success-illustration {
  margin-bottom: var(--Spacing-spacing-4);

  :deep(svg) {
    width: 160px;
    height: auto;
  }
}

.success-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin-bottom: var(--Spacing-spacing-5);
  max-width: 300px;
  text-align: center;
}

.error-state {
  min-height: 200px;
  justify-content: center;
  width: 100%;
}

.loading-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-3);
}

.loading-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin-bottom: var(--Spacing-spacing-5);
}

.loading-icon {
  transform: scale(0.5);
  margin: calc(-1 * var(--Spacing-spacing-4)) 0;
}

.loading-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 400px;
}

.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--Neutral-UI-neutral-ui-10);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: var(--Border-Radius-1);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 var(--Spacing-spacing-4);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes errorShake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
}
</style>
